import { Box, Button } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useState } from "react";
import { z } from "zod";

import type { CollectionSelection } from "@/features/Generations/components/ChooseCollectionModal/taxonomy";
import type { PropagationsListSearch } from "@/features/Generations/propagationList";
import type { Propagation } from "@/types/propagation";

import { ChooseCollectionModal } from "@/features/Generations/components/ChooseCollectionModal/ChooseCollectionModal";
import { PropagationsList } from "@/features/Generations/components/PropagationsList";
import { pilotFlowGroupParamsForPropagation } from "@/features/Generations/pilotFlowGroup";
import {
  generationsStepGroup,
  DEFAULT_STEP_ID_BY_GROUP,
  type GenerationsStepId,
} from "@/features/Generations/steps";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";

const generationsSearchSchema = z.object({
  // `.catch(undefined)` on the string params too: the router's default parser
  // coerces `?q=2024`/`?status=1` in a shared/bookmarked URL to a number, which
  // a bare `z.string()` would reject — throwing out of validateSearch to the
  // root error boundary instead of just rendering the list.
  q: z.string().optional().catch(undefined),
  status: z.string().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(1),
  sort: z.enum(["asc", "desc"]).optional().catch("asc"),
});

export const Route = createFileRoute("/_auth/generations/$siteId/")({
  component: RouteComponent,
  validateSearch: zodValidator(generationsSearchSchema),
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [isCollectionModalOpen, setCollectionModalOpen] = useState(false);

  const handleCollectionSelected = useCallback(
    (selection: CollectionSelection) => {
      setCollectionModalOpen(false);
      void navigate({
        to: "/generations/$siteId/new/pilot/$stepId",
        params: { siteId, stepId: DEFAULT_STEP_ID_BY_GROUP.pilot },
        search: {
          groupId: selection.id,
          groupName: selection.handle,
          groupType: selection.type,
        },
      });
    },
    [navigate, siteId],
  );

  // Memoized so the list's debounced-search effect doesn't re-fire every render.
  const handleSearchChange = useCallback(
    (updates: Partial<PropagationsListSearch>) => {
      void navigate({
        to: "/generations/$siteId",
        params: { siteId },
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [navigate, siteId],
  );

  const handleEdit = (propagation: Propagation, stepId: GenerationsStepId) => {
    // Identify the propagation by slug: the key the API layer (get/update/
    // delete are all slug-keyed) and the flow's `propagationSlug` state use.
    // The group params scope the product list and name the flow breadcrumb.
    const { groupType, groupName, groupId, allProducts } =
      pilotFlowGroupParamsForPropagation(propagation);
    if (generationsStepGroup(stepId) === "pilot") {
      void navigate({
        to: "/generations/$siteId/new/pilot/$stepId",
        params: { siteId, stepId },
        search: {
          propagationId: propagation.slug,
          groupType,
          groupName,
          groupId,
          allProducts,
        },
      });
      return;
    }
    void navigate({
      to: "/generations/$siteId/new/collection/$stepId",
      params: { siteId, stepId },
      search: { propagationId: propagation.slug, groupType, groupName },
    });
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <OrionPageHeader
        breadcrumbs={[{ label: "Creativesphere" }]}
        title="Propagation"
        description="View, edit, and manage story generations and their propagation workflows."
        actions={
          <Button
            variant="contained"
            onClick={() => setCollectionModalOpen(true)}
          >
            Create New Generation
          </Button>
        }
      />
      <PropagationsList
        siteId={siteId}
        search={search}
        onSearchChange={handleSearchChange}
        onEdit={handleEdit}
      />
      <ChooseCollectionModal
        open={isCollectionModalOpen}
        siteId={siteId}
        onClose={() => setCollectionModalOpen(false)}
        onSelect={handleCollectionSelected}
      />
    </Box>
  );
}
